import {goBack} from '@github-ui/history'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {type PaneWithAriaLabel, ThreePanesLayout} from '@github-ui/three-panes-layout'
import {useNavigate} from '@github-ui/use-navigate'
import {Link} from '@primer/react'
import {type Dispatch, type SetStateAction, useCallback, useState} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import type {MilestoneCreateFormRepositoryQuery$key} from './__generated__/MilestoneCreateFormRepositoryQuery.graphql'
import {MilestoneForm} from './components/MilestoneForm'
import {LABELS} from './constants/labels'
import styles from './MilestoneCreateEdit.module.css'
import {MilestoneError} from './MilestoneError'
import type {CreateMilestoneInput} from './mutations/__generated__/createRepositoryMilestoneMutation.graphql'
import {commitCreateRepositoryMilestoneMutation} from './mutations/create-repository-milestone-mutation'
import {formatFormErrorMessage} from './utils/format-form-error-message'

type MilestoneCreateProps = {
  repository: MilestoneCreateFormRepositoryQuery$key
  leftPane?: PaneWithAriaLabel
}

export function MilestoneCreate(props: MilestoneCreateProps) {
  const navigate = useNavigate()
  const environment = useRelayEnvironment()

  const [submissionErrors, setSubmissionErrors] = useState<string | null>(null)

  const repositoryData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment MilestoneCreateFormRepositoryQuery on Repository {
        nameWithOwner
        viewerCanPush
        ...MilestoneFormRepositoryQueryInternal
      }
    `,
    props.repository,
  )

  const handleFormSubmit = useCallback(
    (input: CreateMilestoneInput, setIsSubmitting: Dispatch<SetStateAction<boolean>>) => {
      setSubmissionErrors(null)

      if (!repositoryData?.viewerCanPush) {
        setSubmissionErrors(LABELS.milestoneCreatePermissionError)
        setIsSubmitting(false)
        return
      }

      commitCreateRepositoryMilestoneMutation({
        environment,
        input,
        onError: (error: Error) => {
          setIsSubmitting(false)
          if (error.cause && Array.isArray(error.cause)) {
            setSubmissionErrors(formatFormErrorMessage(error.cause))
          } else {
            setSubmissionErrors(LABELS.milestoneCreateError)
          }
        },
        onCompleted: response => {
          if (!response.createMilestone?.milestone || response?.createMilestone?.errors?.length > 0) {
            setSubmissionErrors(LABELS.milestoneCreateError)
            setIsSubmitting(false)
            return
          }
          navigate(`/${repositoryData.nameWithOwner}/milestone/${response.createMilestone?.milestone.number}`)
        },
      })
    },
    [environment, navigate, repositoryData],
  )

  const handleFormCancel = () => {
    goBack()
  }

  const formDescription = (
    <p className={styles.milestonePageDescription}>
      {LABELS.createMilestoneDescription} {LABELS.learnMorePrefix}{' '}
      <Link href="https://docs.github.com/en/issues/tracking-your-work-with-issues/about-issues" inline>
        {LABELS.milestonesAndIssues}
      </Link>
      .
    </p>
  )

  return (
    <ThreePanesLayout
      contentAs="div"
      resizeable={false}
      leftPaneWidth="medium"
      leftPane={props.leftPane}
      middlePane={
        <div className={styles.middlePaneWrapper} data-hpc data-testid="milestone-create">
          <ErrorBoundary
            fallback={<MilestoneError title={LABELS.milestonePageError} message={LABELS.milestonePageErrorMessage} />}
          >
            <MilestoneForm
              repository={repositoryData}
              formTitle={LABELS.createMilestone}
              formDescription={formDescription}
              formSubmitLabel={LABELS.createMilestone}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              submissionErrors={submissionErrors}
            />
          </ErrorBoundary>
        </div>
      }
    />
  )
}
